import React, { useMemo, useState } from 'react';
import useSWR from 'swr';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Loader2, AlertCircle, CheckCircle2, TriangleAlert, XCircle, Trash2 } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { LibraryLookup } from '@/components/costing/LibraryLookup';
import { formatEuro, formatDate } from '@/lib/format';
import { IMPORT_TYPES } from '@/lib/estimating/templates';
import { getImport, getLines, signedUrl, rejectImport, discardDraft, confirmImport } from '@/lib/estimating/service';
import { RateGrid } from '@/components/estimating/RateGrid';
import { lineColumns } from '@/components/estimating/ImportPreview';
import { StatusBadge } from '@/components/estimating/ImportTypeCard';
import { EST_IDS } from '@/constants/testIds/estimating';

function Tile({ label, children }) {
  return (
    <div className="rounded-lg border border-border bg-white px-4 py-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-charcoal">{children}</div>
    </div>
  );
}

export default function EstimateViewer() {
  const { id: projectId, importId } = useParams();
  const navigate = useNavigate();
  const { role, organisationId } = useAuth();
  const isAdmin = ['owner', 'admin'].includes(role || '');
  const canLookup = ['owner', 'admin', 'manager'].includes(role || '');
  const [lookupLine, setLookupLine] = useState(null);
  const [filter, setFilter] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const { data: imp, error, isLoading, mutate } = useSWR(importId ? ['estimate-import', importId] : null, () => getImport(importId));
  const { data: lines, isLoading: linesLoading, mutate: mutateLines } = useSWR(imp ? ['estimate-lines', imp.id] : null, () => getLines(imp));
  const editable = !!imp && imp.import_type === 'priced_boq' && imp.status === 'draft' && isAdmin;
  const onLineSaved = (saved) => mutateLines((cur) => (cur || []).map((l) => (l.id === saved.id ? { ...l, ...saved } : l)), { revalidate: false });
  const handleConfirmDraft = async () => {
    if (!window.confirm('Confirm this priced BOQ? Rates become read-only and the total is reconciled against the Tender Summary / expected total.')) return;
    try { const c = await confirmImport({ importId: imp.id, overrideReason: rejectReason.trim() || null }); toast.success(`Priced BOQ v${c.version_number} confirmed`); setRejectReason(''); mutate(); mutateLines(); }
    catch (e) { toast.error(e.message); }
  };

  const cols = useMemo(() => (imp ? lineColumns(imp.import_type) : []), [imp]);
  const filtered = useMemo(() => {
    if (!lines) return [];
    const f = filter.trim().toLowerCase();
    if (!f) return lines;
    return lines.filter((l) => Object.values(l).some((v) => typeof v === 'string' && v.toLowerCase().includes(f)));
  }, [lines, filter]);

  const totalKey = imp ? IMPORT_TYPES[imp.import_type].totalKey : null;
  const sum = useMemo(() => filtered.reduce((a, l) => a + (Number(l[totalKey]) || 0), 0), [filtered, totalKey]);

  const handleDownload = async () => {
    try { window.open(await signedUrl(imp.storage_bucket, imp.storage_path), '_blank', 'noopener'); }
    catch (e) { toast.error(e.message || 'Could not create download link'); }
  };
  const handleReject = async () => {
    try { await rejectImport({ importId: imp.id, reason: rejectReason }); toast.success('Draft rejected'); setRejectReason(''); mutate(); }
    catch (e) { toast.error(e.message); }
  };
  const handleDiscard = async () => {
    if (!window.confirm('Discard this draft and its lines? The uploaded file is retained for audit.')) return;
    try { await discardDraft(imp.id); toast.success('Draft discarded'); navigate(`/projects/${projectId}/estimating`); }
    catch (e) { toast.error(e.message); }
  };

  if (isLoading) return <div className="p-8 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Loading…</div>;
  if (error || !imp) {
    return (
      <div className="p-8">
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-center gap-2"><AlertCircle size={16} /> {error?.message || 'Import not found or not accessible.'}</div>
      </div>
    );
  }

  const t = IMPORT_TYPES[imp.import_type];
  const headline = imp.import_type === 'tender_summary' ? imp.tender_total_inc_vat : imp.computed_total;

  return (
    <div data-testid={EST_IDS.viewerPage} className="p-6 md:p-8 max-w-7xl mx-auto">
      <Link to={`/projects/${projectId}/estimating`} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-charcoal mb-4"><ArrowLeft size={14} /> Back to estimating</Link>
      <PageHeader
        title={`${t.label} v${imp.version_number}${imp.is_original ? ' (original)' : ''}`}
        subtitle={<span className="inline-flex items-center gap-2">{imp.file_name} · uploaded {formatDate(imp.uploaded_at)} <StatusBadge status={imp.status} /></span>}
        actions={<div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" data-testid="estimate-viewer-export-pdf" asChild><Link to={`/projects/${projectId}/estimating/${imp.id}/print`}><FileText size={14} className="mr-1" /> Export PDF</Link></Button>
          <Button type="button" variant="outline" size="sm" data-testid={EST_IDS.viewerDownload} onClick={handleDownload}><Download size={14} className="mr-1" /> Original file</Button>
        </div>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Tile label={imp.import_type === 'tender_summary' ? 'Tender total incl. VAT' : imp.import_type === 'priced_boq' ? 'Selling total ex VAT' : 'Total direct cost'}>
          <span className="font-display font-bold text-xl tabular-nums">{formatEuro(headline ?? sum)}</span>
          {imp.import_type === 'tender_summary' && imp.tender_subtotal_ex_vat != null && (
            <div className="text-[11px] text-muted-foreground">Ex VAT {formatEuro(imp.tender_subtotal_ex_vat)} · VAT {formatEuro(imp.tender_vat_amount)}</div>
          )}
        </Tile>
        <Tile label="Expected">
          <span className="font-display font-bold text-xl tabular-nums">{formatEuro(imp.expected_total)}</span>
          <div className="text-[11px] text-muted-foreground">{imp.expected_source === 'tender_summary' ? 'From confirmed Tender Summary' : imp.expected_source === 'manual' ? 'Entered manually' : imp.status === 'draft' ? 'Resolved at confirmation' : 'None'}</div>
        </Tile>
        <Tile label="Reconciliation">
          {imp.status === 'draft' ? <span className="text-sm text-muted-foreground">Pending</span> : imp.reconciled ? (
            <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><CheckCircle2 size={14} /> Reconciled</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-sm text-amber-700"><TriangleAlert size={14} /> Override · variance {formatEuro(imp.reconciliation_variance)}</span>
          )}
          {imp.confirmed_at && <div className="text-[11px] text-muted-foreground">Confirmed {formatDate(imp.confirmed_at)}</div>}
        </Tile>
        <Tile label="Lines"><span className="font-display font-bold text-xl tabular-nums">{lines?.length ?? imp.row_count ?? '—'}</span></Tile>
      </div>

      {imp.reconciliation_override_reason && (
        <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="font-medium">Reconciliation override{imp.reconciliation_failures?.length ? ` (${imp.reconciliation_failures.join(', ')})` : ''}</div>
          <div className="mt-0.5">{imp.reconciliation_override_reason}</div>
        </div>
      )}
      {imp.status === 'rejected' && imp.notes && (
        <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"><span className="font-medium">Rejected:</span> {imp.notes}</div>
      )}

      {imp.status === 'draft' && isAdmin && (
        <div className="mb-6 rounded-lg border border-border bg-white p-4">
          <div className="font-display font-semibold text-charcoal mb-2">{editable ? 'Draft — enter rates' : 'Unconfirmed draft'}</div>
          <p className="text-xs text-muted-foreground mb-3">{editable ? 'Enter a selling rate per line (amount = quantity × rate). Rates save on blur. Confirm when complete — the draft does not affect project figures until confirmed. If the total differs from the Tender Summary, enter an override reason first.' : 'This draft was not confirmed (the import was interrupted). It does not affect project figures. Reject it with a reason to keep it on record, or discard it.'}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Textarea data-testid={EST_IDS.rejectReason} rows={2} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder={editable ? 'Reason (required to reject, or to override a reconciliation mismatch)' : 'Reason for rejecting (required)'} className="flex-1" />
            <div className="flex gap-2">
              {editable && <Button type="button" data-testid="estimate-confirm-draft-button" onClick={handleConfirmDraft} className="bg-bronze hover:bg-bronze-hover text-white"><CheckCircle2 size={14} className="mr-1" /> Confirm BOQ</Button>}
              <Button type="button" variant="outline" data-testid={EST_IDS.rejectButton} disabled={!rejectReason.trim()} onClick={handleReject}><XCircle size={14} className="mr-1" /> Reject</Button>
              <Button type="button" variant="outline" data-testid={EST_IDS.discardButton} onClick={handleDiscard} className="text-red-700"><Trash2 size={14} className="mr-1" /> Discard</Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mb-3">
        <Input data-testid={EST_IDS.viewerFilter} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter lines…" className="h-10 max-w-sm" />
        <div className="text-xs text-muted-foreground tabular-nums">{filtered.length} lines · {formatEuro(sum)}</div>
      </div>

      {editable && !linesLoading ? <RateGrid lines={filtered} onLineSaved={onLineSaved} /> : <div className="rounded-lg border border-border bg-white overflow-x-auto">
        {linesLoading ? (
          <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading lines…</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12 text-xs">Row</TableHead>
                {cols.map((c) => <TableHead key={c.key} className={`text-xs ${c.num ? 'text-right' : ''}`}>{c.header}</TableHead>)}
                {canLookup && imp.import_type === 'priced_boq' && <TableHead className="text-xs w-20">Library</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="text-xs text-muted-foreground">{l.source_row}</TableCell>
                  {cols.map((c) => (
                    <TableCell key={c.key} className={`text-sm ${c.num ? 'text-right tabular-nums' : ''}`}>
                      {c.render ? c.render(l) : c.num ? (l[c.key] != null ? (c.key === 'quantity' ? Number(l[c.key]) : formatEuro(l[c.key])) : '') : (l[c.key] ?? '')}
                    </TableCell>
                  ))}
                  {canLookup && imp.import_type === 'priced_boq' && <TableCell><button type="button" data-testid={`lookup-${l.id}`} onClick={() => setLookupLine(l)} className="text-xs text-bronze hover:underline">Lookup</button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>}
      {lookupLine && <LibraryLookup organisationId={organisationId} line={lookupLine} onClose={() => setLookupLine(null)} />}
    </div>
  );
}
